import { useDispatch, useSelector } from 'react-redux';
import useTranslations from '~/components/translation/use-translations';
import { setShouldShowMarketingConsent } from '../authentication/ducks/index';
import { selectShouldShowMarketingConsent } from '../authentication/ducks/selectors';
import Binary from '~/components/modal/binary';
import { updateAcceptMarketingFlag } from './index';
import { useCallback, useEffect } from 'react';
import { insertTrackEvent } from '@comeon/analytics';
import { reportException } from '@comeon/error-reporter';
import { ErrorReporterDomain } from '../error-handling/error-reporter-domain';

const TEXT_KEYS = [
	'MARKETING_CONSENT_POPUP_HEADER',
	'MARKETING_CONSENT_POPUP_DESCRIPTION',
	'MARKETING_CONSENT_POPUP_CTA_BUTTON',
	'BUTTON_CANCEL'
];

const MarketingConsentPopup: React.FC = () => {
	const dispatch = useDispatch();
	const shouldShowMarketingConsent = useSelector(selectShouldShowMarketingConsent);
	const { translations: t, isLoadingTranslations } = useTranslations(TEXT_KEYS);

	useEffect(() => {
		if (shouldShowMarketingConsent) {
			insertTrackEvent({
				category: 'Notification',
				action: 'Oasis Marketing Consent Pop-up',
				label: 'Displayed'
			});
		}
	}, [shouldShowMarketingConsent]);

	const closePopup = useCallback(() => {
		dispatch(setShouldShowMarketingConsent(false));
	}, [dispatch]);

	const onReject = useCallback(() => {
		insertTrackEvent({
			category: 'Notification',
			action: 'Oasis Marketing Consent Pop-up',
			label: 'Click - Cancel'
		});
		closePopup();
	}, [closePopup]);

	const updateMarketingConsent = useCallback(() => {
		insertTrackEvent({
			category: 'Notification',
			action: 'Oasis Marketing Consent Pop-up',
			label: 'Click - Accept'
		});
		try {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			updateAcceptMarketingFlag(true);
		} catch (error) {
			reportException(new Error('Update accept marketing flag error', { cause: error }), {
				domain: ErrorReporterDomain.ACCOUNT
			});
		} finally {
			closePopup();
		}
	}, [closePopup]);

	if (!shouldShowMarketingConsent || isLoadingTranslations) {
		return null;
	}

	return (
		<Binary
			title={t.MARKETING_CONSENT_POPUP_HEADER}
			message={t.MARKETING_CONSENT_POPUP_DESCRIPTION}
			confirmButtonText={t.MARKETING_CONSENT_POPUP_CTA_BUTTON}
			rejectButtonText={t.BUTTON_CANCEL}
			onConfirm={updateMarketingConsent}
			onReject={onReject}
		/>
	);
};

export default MarketingConsentPopup;
